import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmTrackerService } from '@shared/services';
import { ConfirmationService } from 'primeng/api';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonComponent } from '../button/button.component';

@Component({
    selector: 'btn-confirm',
    standalone: true,
    imports: [ButtonComponent, TranslateModule, ConfirmPopupModule, TooltipModule],
    templateUrl: './button-confirm.component.html',
    styleUrl: './button-confirm.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ButtonConfirmComponent {
    private confirmationService = inject(ConfirmationService);
    private translateService = inject(TranslateService);
    private tracker = inject(ConfirmTrackerService);

    id = input.required<string>();

    severity = input<'success' | 'warn' | 'danger' | 'primary' | 'secondary'>('danger');
    size = input<'small' | 'large' | undefined>(undefined);
    text = input<boolean>(true);
    rounded = input<boolean>(true);
    outlined = input<boolean>(false);
    loading = input<boolean>(false);
    disabled = input<boolean>(false);

    tooltip = input<string>('');
    tooltipPosition = input<'bottom' | 'left' | 'right' | 'top'>('top');
    tooltipDisabled = input<boolean>(false);

    label = input<string>('');
    icon = input<string>('');
    hideIcon = input<boolean>(false);

    fullWidth = input<boolean>(false);

    message = input<string>('uiKit.buttons.buttonConfirm.confirmDelete');

    acceptLabel = input<string>('uiKit.buttons.buttonConfirm.acceptLabel');
    rejectLabel = input<string>('uiKit.buttons.buttonConfirm.rejectLabel');

    acceptStyleClass = input<string>('p-button-danger');
    rejectStyleClass = input<string>('p-button-outlined p-button-secondary');

    accept = output<void>();
    reject = output<void>();

    popconfirmIcon = computed(() => {
        switch (this.severity()) {
            case 'success':
                return 'fa-regular fa-circle-check';
            case 'warn':
                return 'fa-regular fa-triangle-exclamation';
            case 'danger':
                return 'fa-regular fa-do-not-enter';
            case 'primary':
                return 'fa-regular fa-circle-info';
            case 'secondary':
                return 'fa-regular fa-circle-info secondary';
        }
    });

    openPopconfirm(event: MouseEvent): void {
        this.tracker.register(this);

        this.confirmationService.confirm({
            target: event.target as EventTarget,
            message: this.translateService.instant(this.message()),
            icon: this.popconfirmIcon(),
            key: `${this.id()}-btn`,
            rejectLabel: this.translateService.instant(this.rejectLabel()),
            acceptLabel: this.translateService.instant(this.acceptLabel()),
            rejectButtonStyleClass: this.rejectStyleClass(),
            acceptButtonStyleClass: this.acceptStyleClass(),
            accept: () => {
                this.accept.emit();
            },
            reject: () => {
                this.tracker.clear(this);
                this.reject.emit();
            },
        });
    }

    closePopconfirm() {
        this.confirmationService.close();
        this.tracker.clear(this);
    }
}
